import { useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Popover } from "radix-ui";
import type { GifAttachment, GifPage, GifResult } from "@atrium/shared";
import { api, errorMessage, qs } from "../../lib/api";
import { useDebounced, useInView } from "../../lib/hooks";
import { useMotionPrefs } from "../../lib/prefs";
import { Spinner } from "../../components/ui/Spinner";
import { Tooltip } from "../../components/ui/Tooltip";
import { IconSearch } from "../../components/icons";
import { useServerConfig } from "./serverConfig";

const COLS = 2;

function GifTile({ gif, animate, onPick, onKeyDown, index }: { gif: GifResult; animate: boolean; onPick: () => void; onKeyDown: (e: KeyboardEvent<HTMLButtonElement>, i: number) => void; index: number }) {
  const [hover, setHover] = useState(false);
  const src = animate || hover || !gif.stillUrl ? gif.previewUrl : gif.stillUrl;
  return (
    <button
      type="button"
      role="gridcell"
      data-gif-index={index}
      aria-label={gif.title || "GIF"}
      onClick={onPick}
      onKeyDown={(e) => onKeyDown(e, index)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      className="relative h-28 overflow-hidden rounded-md bg-surface-2 outline-none ring-accent focus-visible:ring-2"
    >
      <img src={src} alt="" loading="lazy" className="size-full object-cover" draggable={false} />
    </button>
  );
}

/**
 * GIF search (trending when empty) in a popover. Picking a GIF sends it right
 * away. Arrow keys move through results; ↓ from the search box enters the grid.
 */
export function GifPicker({ children, tooltip, onPick, open, onOpenChange }: { children: ReactNode; tooltip?: string; onPick: (gif: GifAttachment) => void; open: boolean; onOpenChange: (o: boolean) => void }) {
  const [q, setQ] = useState("");
  const dq = useDebounced(q.trim(), 300);
  const { gifAutoplay } = useMotionPrefs();
  const config = useServerConfig();
  const gridRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const query = useInfiniteQuery({
    queryKey: ["gifs", dq],
    queryFn: ({ pageParam, signal }) =>
      api.get<GifPage>(dq ? `/api/gifs/search${qs({ q: dq, cursor: pageParam as string | undefined })}` : `/api/gifs/trending${qs({ cursor: pageParam as string | undefined })}`, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  const sentinel = useInView<HTMLDivElement>(() => query.hasNextPage && !query.isFetchingNextPage && void query.fetchNextPage(), !!query.hasNextPage, "200px");

  const pick = (g: GifResult) => {
    onPick({ id: g.id, url: g.url, previewUrl: g.previewUrl, stillUrl: g.stillUrl, width: g.width, height: g.height, title: g.title });
    onOpenChange(false);
  };

  const focusTile = (i: number) => gridRef.current?.querySelector<HTMLButtonElement>(`[data-gif-index="${i}"]`)?.focus();
  const onTileKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const moves: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: COLS, ArrowUp: -COLS };
    if (!(e.key in moves)) return;
    e.preventDefault();
    const next = i + moves[e.key];
    if (next < 0) return inputRef.current?.focus();
    if (next < items.length) focusTile(next);
  };

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Tooltip label={tooltip} disabled={!tooltip || open}>
        <Popover.Trigger asChild>{children}</Popover.Trigger>
      </Tooltip>
      <Popover.Portal>
        <Popover.Content
          side="top"
          align="end"
          sideOffset={8}
          collisionPadding={8}
          className="z-[60] flex h-[420px] max-h-[70dvh] w-[360px] max-w-[calc(100vw-16px)] flex-col rounded-lg bg-surface shadow-pop animate-pop-in"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <div className="p-2">
            <div className="relative">
              <IconSearch className="icon pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-fg-3" aria-hidden />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown" && items.length) {
                    e.preventDefault();
                    focusTile(0);
                  }
                  if (e.key === "Enter" && items[0]) {
                    e.preventDefault();
                    pick(items[0]);
                  }
                }}
                placeholder="Search GIFs"
                aria-label="Search GIFs"
                maxLength={100}
                className="h-9 w-full rounded-md bg-surface-2 pl-8 pr-3 text-sm text-fg placeholder:text-fg-3 focus:outline-none focus:ring-1 focus:ring-line-strong"
              />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
            {query.isPending ? (
              <div className="flex h-full items-center justify-center text-fg-3">
                <Spinner className="size-4" />
              </div>
            ) : query.isError ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-fg-3">
                {errorMessage(query.error, "GIFs couldn't be loaded")}
                <button type="button" onClick={() => void query.refetch()} className="font-medium text-fg-2 hover:text-fg">
                  Retry
                </button>
              </div>
            ) : !items.length ? (
              <p className="flex h-full items-center justify-center text-sm text-fg-3">No GIFs found</p>
            ) : (
              <div ref={gridRef} role="grid" aria-label={dq ? `GIFs for ${dq}` : "Trending GIFs"} className="grid grid-cols-2 gap-1.5">
                {items.map((g, i) => (
                  <GifTile key={`${g.id}-${i}`} gif={g} index={i} animate={gifAutoplay} onPick={() => pick(g)} onKeyDown={onTileKey} />
                ))}
                <div ref={sentinel} className="col-span-2 flex h-8 items-center justify-center text-fg-3">
                  {query.isFetchingNextPage && <Spinner className="size-4" />}
                </div>
              </div>
            )}
          </div>
          {config.data?.gifs?.provider === "giphy" && <div className="border-t border-line px-3 py-1.5 text-right text-xs text-fg-3">Powered by GIPHY</div>}
          {config.data?.gifs?.provider === "tenor" && <div className="border-t border-line px-3 py-1.5 text-right text-xs text-fg-3">Via Tenor</div>}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
